import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { cloneDeep, isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { exportImage,Image, geometry } from '@progress/kendo-drawing';
import { saveAs } from '@progress/kendo-file-saver';
import { getTimeZoneAbbr } from '../../../../helpers/timezones';

import BobLoadingMask from '../../../../components/BobLoadingMask';

import {
    Chart,
    ChartLegend,
    ChartSeries,
    ChartSeriesItem,
    ChartValueAxis,
    ChartValueAxisItem,
    ChartCategoryAxis,
    ChartCategoryAxisItem,
    ChartTooltip,
    ChartTitle,
    exportVisual
} from '@progress/kendo-react-charts';

import classes from './ScoreGraph.module.css';

const ScoreGraph = ({
    allSensors,
    bobLoading,
    dashboardEndTimeStamp,
    entityScoringData,
    isActive,
    rawUnits,
    selectedRuleSensors,
    selectedEquipment,
    selectedEquipmentRule,
    ...props
}) => {
    const [scores, setScores] = useState([]);
    const [scoreChartHeight, setScoreChartHeight] = useState('60vh');
    const chart = React.createRef();
    const { Rect, Point, Size } = geometry;
    const minWidth2560 = matchMedia('(min-width: 2560px');
    const zoom125 = matchMedia('(min-resolution: 1.25dppx');

    const updateScoreChart2560 = (e) => {
        if (e.matches) {
            setScoreChartHeight('71vh');
        }
        else {
            setScoreChartHeight('60vh');
        }

    };

    const mqlZoom125 = (e) => {
        if (e.matches) {
            setScoreChartHeight('49vh');
        }
        else {
            setScoreChartHeight('60vh');
        }
    };

    useEffect(() => {
        return () => {
            minWidth2560.removeEventListener('change', updateScoreChart2560);
            zoom125.removeEventListener('change', mqlZoom125);
        };
    }, []);

    useEffect(() => {
        if (window.devicePixelRatio === 1) {
            setScoreChartHeight('60vh');
        }
        else {
            setScoreChartHeight('49vh');
        }

        minWidth2560.addEventListener('change', updateScoreChart2560);
        zoom125.addEventListener('change', mqlZoom125);

        const mediaQuery = window.matchMedia('(min-width: 2560px)');
        if (mediaQuery.matches) {
            updateScoreChart2560(mediaQuery);
        }

    }, []);

    useEffect(() => {
        let data = [];
        let entityScoringDataCopy = cloneDeep(entityScoringData);
        let numericData = [];
        if (!isEmpty(entityScoringDataCopy)) {
            Object.keys(entityScoringDataCopy[0]).forEach(item => {
                let id = item.split('_');
                id = id[1];
                if (item.includes('Name') && (item.includes('rule') && !item.includes('ruleFvoSensor'))) {
                    Object.keys(entityScoringDataCopy[0]).forEach(point => {
                        if (point.includes(id) && point.includes('Val')) {
                            data.push({
                                name: entityScoringDataCopy[0][item],
                                key:  point,
                                axis: 'Left Y Axis',
                                data: []
                            });
                        }
                    });
                }

                if (item.includes('overallScore')) {
                    data.unshift({
                        name: 'Overall Score',
                        axis: 'Left Y Axis',
                        key:  item,
                        data: []
                    });
                }
            });

            data.forEach(item => {
                entityScoringDataCopy.forEach(score => {
                    let entry = {
                        timestamp: new Date(score['start']),
                        value:     !isNil(score[item.key]) ? renderValue(score, item) : null,
                        unit:      !isNil(score[item.key]) ? renderUnit(score, item) : null
                    };
                    item.data.push(entry);
                });
                numericData.push(item);
            });
        }

        setScores(numericData);
    }, [entityScoringData]);

    const getUnitDisplayValue = (rawUnitId) => {
        let targetUnit;
        if (rawUnits) {
            rawUnits.forEach(unit => {
                unit.items.forEach(item => {
                    if (item.id === rawUnitId) {
                        targetUnit = { ...item };
                    }
                });
            });
        }
        return targetUnit ? targetUnit.displayValue : '-';
    };

    const renderUnit = (score, item) => {
        if (item.key.includes('sensor')) {
            let id = item.key.split('_');
            if (!id) return;
            const selectedSensor = allSensors.find(sensor => sensor.id === id[1]);

            if (selectedSensor) return selectedSensor.presentationUnitId;
            return null;
        }
    };

    const renderValue = (score, item) => {
        if (score[item.key] === 'true') {
            return true;
        } else if (score[item.key] === 'false') {
            return false;
        } else {
            if (item.key.toLowerCase().includes('sensor')) {
                return score[item.key];
            } else {
                return score[item.key] * 100;
            }
        }
    };

    const downloadChart = () =>{
        if (chart) {
            let chartRefValue = chart.current;
            if (chartRefValue && chartRefValue.element) {
                const imageRect = new Rect(new Point(chartRefValue.element.offsetWidth-110, chartRefValue.element.offsetHeight), new Size(110, 30));
                const imageUrl = `/assets/images/k2acopyright.png`;
                const image = new Image(imageUrl, imageRect);
                chartRefValue.surface.draw(image);
                const chartVisual = exportVisual(chartRefValue);
                if (chartVisual) {
                    exportImage(chartVisual).then(dataURI => saveAs(dataURI, 'Score graph'));
                }
            }
        }
    };

    props.propsFunc.downloadChart = downloadChart;

    const SharedTooltip = (props) => {
        const zone = moment.tz.guess();
        const pointDate = props.points[0].category;
        let points = props.points.filter(c => c.series.axis !== 'bottom');

        let group = points.reduce((r, a) => {
            if (!a.series.axis) {
                a.series.axis = 'Axis';
            }
            r[a.series.axis] = [...r[a.series.axis] || [], a];
            return r;
        }, {});

        let finalArray = [];
        let booleanobj = null;
        for (var pt in group) {
            if (Object.prototype.hasOwnProperty.call(group, pt) && pt !== 'Boolean') {
                finalArray.push({ axis: pt, points: group[pt].sort((a, b) => { return a.series.name.localeCompare(b.series.name); }) });
            }
            else if (pt === 'Boolean') {
                booleanobj = { axis: pt, points: group[pt].sort((a, b) => { return a.series.name.localeCompare(b.series.name); }) };
            }
        }
        if (booleanobj) {
            finalArray.push(booleanobj);
        }

        let marginTop = -40;
        switch (points.length) {
            case 1:
                marginTop = 300;
                break;
            case 2:
                marginTop = 275;
                break;
            case 3:
                marginTop = 260;
                break;
            case 4:
                marginTop = 245;
                break;
            case 5:
                marginTop = 230;
                break;
            case 6:
                marginTop = 215;
                break;
            case 7:
                marginTop = 190;
                break;
            case 8:
                marginTop = 175;
                break;
            case 9:
                marginTop = 150;
                break;
            case 10:
                marginTop = 130;
                break;
            case 11:
                marginTop = 110;
                break;
            default:
                marginTop = 300;
        }
        let root = document.documentElement;
        root.style.setProperty('--sharedTooltipTop', marginTop + 'px');

        const renderItemValue = point => {
            let value = point.dataItem.value;

            if (value === true) {
                return 'True';
            } else if (value === false) {
                return 'False';
            } else {
                value = value != null && value !== '' ? value.toString().includes('.') ? parseFloat(value).toFixed(2) : value : '';
                return point.series.axis === 'Left Y Axis' ? value + '%' : value + getUnitDisplayValue(point.series.unit);
            }
        };

        return (
            <div>
                <style>
                    {
                        `
                            .k-chart-shared-tooltip {
                                position: fixed;
                                top: var(--sharedTooltipTop);
                                margin-left: -50px;
                                min-width: auto;
                                padding-bottom: 10px;
                            }
                        `
                    }
                </style>
                <React.Fragment>
                    <div style={{color: '#000000', fontWeight: 'bold', height: 20, marginBottom: 8}}>
                        {points && points.length > 0 ?
                            moment(pointDate).format('YYYY-MM-DD h:mm A') + ' ' + getTimeZoneAbbr(moment(pointDate), zone)
                            : ''
                        }
                    </div>
                    {
                        finalArray.map((c, index) => (
                            <React.Fragment key={index}>
                                <React.Fragment>
                                    {c.points.map((point) => (
                                        <div key={Math.random()} style={{overflowWrap: 'break-word', height: 20, color: point.series && point.series.color ? point.series.color : '#000000'}}>
                                            {
                                                point.series.axis === 'Boolean' ?
                                                    <React.Fragment>{point.series.name
                                                        ? <React.Fragment>{
                                                            `${ point.series.name } - ${ point.dataItem.booleanValue === true ?
                                                                'True' : point.dataItem.booleanValue === null ? 'No Data' : 'False' }`
                                                        }
                                                        </React.Fragment>
                                                        : null
                                                    }
                                                    </React.Fragment>
                                                    : <React.Fragment>
                                                        <React.Fragment>{point.series.name ?
                                                            <React.Fragment>{`${ point.series.name } - `}</React.Fragment>
                                                            : null
                                                        }
                                                        </React.Fragment>
                                                        <React.Fragment>
                                                            {`${ renderItemValue(point) }`}
                                                        </React.Fragment>
                                                    </React.Fragment>
                                            }
                                        </div>))
                                    }
                                </React.Fragment>
                            </React.Fragment>
                        ))}
                </React.Fragment>
            </div>
        );
    };


    const sharedTooltipRender = (context) => (<SharedTooltip {...context} />);

    const renderAxisCrossingValue = () => {
        let axisCrossingValue = [];

        if (!isEmpty(scores)) {
            if (!scores[0].axis) {
                return [];
            }
        }

        scores.map(item =>
            axisCrossingValue.push(new Date(moment(dashboardEndTimeStamp).add(1, 'days')))
        );

        axisCrossingValue.pop();
        axisCrossingValue.push(0);

        return axisCrossingValue.reverse();
    };

    const renderChartTitle = () => {
        if (selectedEquipment.name && selectedEquipmentRule.ruleName) {
            return `${selectedEquipment.name} - ${selectedEquipmentRule.ruleName} - Score`;
        }
        return 'Score';
    };

    if (!isActive) return null;

    return (
        <div className={classes.ScoresPanel}>
            <div className={classes.TableContainer}>
                <Chart
                    style={{
                        height: scoreChartHeight
                    }}
                    ref = {chart}
                    pannable={{ lock: 'y' }}
                    zoomable={{ mousewheel: { lock: 'y' }, selection: { lock: 'y' } }}
                >
                    <ChartTitle text={renderChartTitle()} align="left" font="12pt sans-serif" />
                    <ChartTooltip
                        shared={true} visible = {true} render={sharedTooltipRender}
                        background="#FFFFFF"
                        opacity={.95}
                    />
                    <ChartCategoryAxis>
                        <ChartCategoryAxisItem
                            baseUnit="minutes"
                            baseUnitStep={15}
                            pane="top"
                            maxDivisions={12}
                            labels={{ visible: true}}
                            axisCrossingValue={renderAxisCrossingValue()}
                        />
                    </ChartCategoryAxis>
                    <ChartLegend position="top" orientation="horizontal"  visible={true} />
                    <ChartSeries>
                        {scores.map((item, index) => (
                            <ChartSeriesItem
                                axis={item.axis}
                                markers={{ visible: false }}
                                type="line"
                                categoryField="timestamp"
                                name={item.name}
                                key={index}
                                data={item.data}
                                field="value"
                                unit={item.unit}
                                // eslint-disable-next-line react/style-prop-object
                                style="smooth"
                            />
                        ))}
                    </ChartSeries>
                    <ChartValueAxis>
                        <ChartValueAxisItem
                            key={0}
                            name={'Left Y Axis'}
                            title={{ text: 'Score' }}
                            narrowRange={true}
                            majorGridLines={
                                { width: 1, dashType: 'dot' }
                            }
                        />
                    </ChartValueAxis>
                </Chart>
                {
                    bobLoading.includes('ScoreData') || bobLoading.includes('SensorIds') || bobLoading.includes('RuleKpi') ?
                        <BobLoadingMask />
                        :
                        null
                }
            </div>
        </div>
    );
};

const mapStateToProps = state => {
    return {
        allSensors:            state.bob.allSensors,
        bobLoading:            state.bob.bobLoading,
        dashboardEndTimeStamp: state.dashboard.dashboardEndTimeStamp,
        entityScoringData:     state.bob.entityScoringData,
        entityScoresData:      state.bob.entityScoresData,
        rawUnits:              state.architect.rawUnits,
        selectedRuleSensors:   state.dashboard.selectedRuleSensors
    };
};

export default connect(mapStateToProps, null)(ScoreGraph);
